import { ConfirmationDialogBox, Grid, Stack } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button } from '@components';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { closeModal } from '@store/modals';
import {
  deleteOrderTemplate,
  fetchOrderTemplateData,
  orderStore,
  setOrderStore,
} from '@store/orders';
import { DeleteIcon, PencilIcon, AccountGroupIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { DELETE_ORDER_TEMPLATE_DIALOG_ID } from '@views/order/constants';
import { size } from 'lodash';
import { DateTime } from 'luxon';
import { createSignal } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

export const TemplateListModal = () => {
  const [orderTemplateIdToDelete, setOrderTemplateIdToDelete] =
    createSignal<number>(0);
  const [templateIdInfoLoading, setTemplateIdInfoLoading] = createSignal<
    number | null
  >(null);

  const handleOrderTemplateDelete = async () => {
    await deleteOrderTemplate(
      orderTemplateIdToDelete(),
      CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderDeleteTemplate,
    );
  };

  const handleOrderTemplateEdit = async (templateId: number) => {
    setTemplateIdInfoLoading(templateId);
    await fetchOrderTemplateData(templateId, () => {
      setOrderStore('isTemplateMode', true); //this is to put isTemplateMode to true to control different views
      setTemplateIdInfoLoading(null);
    });
    closeModal('orderTemplateModal');
  };

  return (
    <BasicModal
      id={'orderTemplateModal'}
      title="Manage Templates"
      width="600px"
      footer={false}
    >
      <div class="overflow-auto h-[400px]">
        {orderStore.orderTemplates.map((template, idx) => {
          return (
            <Grid container class="p-1!">
              <Grid item size={template.editable ? 8 : 12}>
                <Stack>
                  <span class="text-[20px]">{template.name}</span>
                  <span class="text-[18px]">
                    Owner: <b>{template.owner}</b>{' '}
                    {template.public && (
                      <AccountGroupIcon color="green" class="inline-block" />
                    )}
                  </span>
                  <span>
                    Created On:{' '}
                    {DateTime.fromISO(template.createdDate).toFormat(
                      'MM/dd/yyyy',
                    )}
                  </span>
                </Stack>
              </Grid>
              {template.editable && (
                <Grid item size={4}>
                  <Grid class="text-right!">
                    <Button
                      label="Edit"
                      size="sm"
                      startIcon={<PencilIcon size={20} />}
                      isLoading={
                        orderStore.loading &&
                        templateIdInfoLoading() === template.id
                      }
                      onClick={() => handleOrderTemplateEdit(template.id)}
                      dataTestId={formatDataTestId('Edit', 'button')}
                    />
                    <span
                      onClick={() => {
                        setOrderTemplateIdToDelete(template.id);
                        openDialogBox(DELETE_ORDER_TEMPLATE_DIALOG_ID);
                      }}
                    >
                      <DeleteIcon color="red" class="cursor-pointer ml-2" />
                    </span>
                  </Grid>
                </Grid>
              )}
              {idx !== size(orderStore.orderTemplates) - 1 && (
                <div class="my-2 w-full border-t border-gray-300" />
              )}
            </Grid>
          );
        })}
        <ConfirmationDialogBox
          closeDialogBox={(_reason) =>
            closeDialogBox(DELETE_ORDER_TEMPLATE_DIALOG_ID)
          }
          isDialogOpen={checkIfDialogBoxIsOpen(DELETE_ORDER_TEMPLATE_DIALOG_ID)}
          dataTestId={DELETE_ORDER_TEMPLATE_DIALOG_ID}
          id={DELETE_ORDER_TEMPLATE_DIALOG_ID}
          title={`Are you sure you want to delete this template ${orderTemplateIdToDelete()}?`}
          onSubmit={() => handleOrderTemplateDelete()}
          onSubmitText="Ok"
        />
      </div>
    </BasicModal>
  );
};
